import { useEffect, useState } from 'react';
import { MAX_PHOTO_CAPTION_LENGTH, type PlantPhoto } from '../../types/photo';
import { formatDate } from '../../utils/formatters';
import { useKeyboardInset } from '../../hooks/useKeyboardInset';

interface PhotoViewerProps {
  photo: PlantPhoto | null;
  plantName: string;
  onClose: () => void;
  onSetPrimary: (photoId: string) => Promise<void>;
  onSaveCaption: (photoId: string, caption: string) => Promise<void>;
  onDelete: (photoId: string) => Promise<void>;
}

/**
 * Full-screen view of a single gallery photo, with the actions that only make
 * sense on one photo at a time: set as key photo, caption, delete.
 *
 * The caption editor is inline rather than a nested `BottomSheet` — this view
 * is already a modal layer, and stacking a Radix dialog inside it risks the
 * portal/animation fragility documented for BottomSheet without buying any
 * usability on a phone.
 */
export function PhotoViewer({
  photo,
  plantName,
  onClose,
  onSetPrimary,
  onSaveCaption,
  onDelete,
}: PhotoViewerProps) {
  const [caption, setCaption] = useState('');
  const [editingCaption, setEditingCaption] = useState(false);
  const [busy, setBusy] = useState<null | 'primary' | 'caption' | 'delete'>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Reset per-photo state whenever a different photo is opened.
  useEffect(() => {
    setCaption(photo?.caption ?? '');
    setEditingCaption(false);
    setConfirmingDelete(false);
    setBusy(null);
  }, [photo]);

  // Escape closes; lock body scroll while open so the page behind can't move.
  useEffect(() => {
    if (!photo) return;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [photo, onClose]);

  // The caption editor sits in the bottom section, which the keyboard would
  // otherwise cover. Must run before the early return below — hooks cannot be
  // called conditionally.
  const keyboardInset = useKeyboardInset(!!photo);

  if (!photo) return null;

  const run = async (kind: 'primary' | 'caption' | 'delete', action: () => Promise<void>) => {
    if (busy) return;
    setBusy(kind);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-editorial-black/95"
      style={{ paddingBottom: keyboardInset }}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo of ${plantName}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 safe-top">
        <button
          onClick={onClose}
          className="touch-target rounded-full p-2 text-white transition-colors hover:bg-white/20"
          aria-label="Close photo"
        >
          <span className="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
        {photo.isPrimary && (
          <span className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 font-sans text-xs font-bold uppercase tracking-widest text-editorial-black">
            <span className="material-symbols-outlined text-sm" aria-hidden="true">star</span>
            Key photo
          </span>
        )}
      </div>

      {/* Photo */}
      <div className="flex flex-1 items-center justify-center overflow-hidden px-4">
        <img
          src={photo.url}
          alt={photo.caption ? `${plantName} — ${photo.caption}` : `${plantName} - user photo`}
          className="max-h-full max-w-full rounded-lg object-contain"
        />
      </div>

      {/* Meta + actions */}
      <div className="space-y-4 px-4 py-5 safe-bottom">
        <p className="font-sans text-xs uppercase tracking-widest text-white/60">
          Added {formatDate(photo.createdAt, 'recently')}
        </p>

        {editingCaption ? (
          <div>
            <label htmlFor="photo-caption" className="mb-2 block font-sans text-xs font-medium uppercase tracking-widest text-white/60">
              Caption
            </label>
            <textarea
              id="photo-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={MAX_PHOTO_CAPTION_LENGTH}
              rows={2}
              placeholder="Repotted, new leaf, moved to the window…"
              className="w-full rounded-lg border border-white/20 bg-white/10 p-3 font-serif text-base text-white placeholder:text-white/40 focus:border-primary focus:outline-none"
            />
            <div className="mt-3 flex gap-3">
              <button
                onClick={() =>
                  run('caption', async () => {
                    await onSaveCaption(photo.id, caption);
                    setEditingCaption(false);
                  })
                }
                disabled={busy !== null}
                className="btn btn-primary flex-1 text-xs disabled:opacity-50"
                aria-label="Save caption"
              >
                {busy === 'caption' ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setCaption(photo.caption ?? '');
                  setEditingCaption(false);
                }}
                disabled={busy !== null}
                className="touch-target flex-1 rounded-lg border border-white/30 font-sans text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-white disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setEditingCaption(true)}
            className="touch-target flex w-full items-center gap-2 rounded-lg border border-white/20 px-3 py-2 text-left transition-colors hover:border-white/50"
            aria-label={photo.caption ? 'Edit caption' : 'Add a caption'}
          >
            <span className="material-symbols-outlined text-white/60" style={{ fontSize: 20 }} aria-hidden="true">
              edit_note
            </span>
            <span className={`font-serif text-base ${photo.caption ? 'text-white' : 'text-white/50'}`}>
              {photo.caption || 'Add a caption'}
            </span>
          </button>
        )}

        {!editingCaption && (
          <div className="flex gap-3">
            {!photo.isPrimary && (
              <button
                onClick={() => run('primary', () => onSetPrimary(photo.id))}
                disabled={busy !== null}
                className="btn btn-primary flex-1 text-xs disabled:opacity-50"
                aria-label={`Make this the key photo for ${plantName}`}
              >
                {busy === 'primary' ? 'Setting…' : 'Set as key photo'}
              </button>
            )}
            <button
              onClick={() => {
                if (!confirmingDelete) {
                  setConfirmingDelete(true);
                  return;
                }
                // Disarm afterwards either way. On success the viewer closes so
                // it doesn't matter; on a blocked delete the button must stop
                // saying "tap again to delete" for something that can't be.
                run('delete', () => onDelete(photo.id)).then(() => setConfirmingDelete(false));
              }}
              disabled={busy !== null}
              className={`touch-target rounded-lg border px-4 font-sans text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50 ${
                confirmingDelete
                  ? 'flex-1 border-danger bg-danger text-white'
                  : 'border-white/30 text-white hover:border-white'
              }`}
              aria-label={confirmingDelete ? 'Confirm delete photo' : 'Delete photo'}
            >
              {busy === 'delete' ? 'Deleting…' : confirmingDelete ? 'Tap again to delete' : 'Delete'}
            </button>
          </div>
        )}

        <div role="status" aria-live="polite" className="sr-only">
          {busy === 'primary' && 'Setting key photo'}
          {busy === 'caption' && 'Saving caption'}
          {busy === 'delete' && 'Deleting photo'}
        </div>
      </div>
    </div>
  );
}
